import { getTheme } from '@fluentui/react';
import DOMPurify from 'dompurify';
import parse, { attributesToProps, DOMNode, domToReact, HTMLReactParserOptions } from 'html-react-parser';
import { convert } from 'html-to-text';
import linkifyHtml from 'linkify-html';
import React from 'react';
import { truncate } from './string';

const BLOCK_TAGS = new Set([
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'blockquote',
  'pre',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'hr',
  'figure',
  'figcaption',
  'section',
  'article',
  'aside',
  'nav',
  'main',
  'header',
  'footer',
  'address',
  'dl',
  'dt',
  'dd',
  'div',
]);

const VOID_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

/// <summary>
/// Whenever you parse HTML into React:
/// - Always re-render anchor tags with a theme-aware color so links remain
///   readable in dark mode (browser default link colors disappear against
///   dark surfaces).
/// - Optionally, find block-level elements and recreate them with margin: 0,
///   while keeping their other props and recursively doing the same for
///   nested content.
/// </summary>
const replaceNode = (
  node: DOMNode,
  noBlockMargins: boolean,
  childOptions: HTMLReactParserOptions,
): JSX.Element | undefined => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const n = node as any;
  if (!n || n.type !== 'tag' || typeof n.name !== 'string') return undefined;

  // Anchor tags: apply theme-aware color so links are readable in dark mode.
  // theme.semanticColors.link is the canonical Fluent token for link color and
  // swaps automatically per active theme (light / dark).
  if (n.name === 'a') {
    const theme = getTheme();
    const props = attributesToProps(n.attribs ?? {});
    const style = props.style && typeof props.style === 'object' && !Array.isArray(props.style) ? props.style : {};

    return React.createElement(
      'a',
      {
        ...props,
        style: { ...style, color: theme.semanticColors.link },
      },
      domToReact(n.children, childOptions),
    );
  }

  // Block tags: optionally strip margin.
  if (noBlockMargins && BLOCK_TAGS.has(n.name)) {
    const props = attributesToProps(n.attribs ?? {});
    const style = props.style && typeof props.style === 'object' && !Array.isArray(props.style) ? props.style : {};
    const mergedProps = { ...props, style: { ...style, margin: 0 } };

    if (VOID_TAGS.has(n.name)) {
      return React.createElement(n.name, mergedProps);
    }

    return React.createElement(n.name, mergedProps, domToReact(n.children, childOptions));
  }

  return undefined;
};

const themedHtmlOptions: HTMLReactParserOptions = {
  replace: (node) => replaceNode(node, false, themedHtmlOptions),
};

export const noBlockMarginOptions: HTMLReactParserOptions = {
  replace: (node) => replaceNode(node, true, noBlockMarginOptions),
};

export const stripHtmlParagraph = (html: string | undefined): string | undefined => {
  if (!html) return html;

  const trimmed = html.trim();
  const match = trimmed.match(/^<p\b[^>]*>([\s\S]*)<\/p>$/i);

  if (!match) {
    return trimmed;
  }

  return match[1];
};

export const sanitizeHtml = (html: string): string => {
  // DOMPurify strips the target attribute, but we want to allow target=_blank for links, so users can configure to open links in a new tab
  // Ensure we only allow target=_blank and add rel="noopener noreferrer" for security reasons
  const clean = DOMPurify.sanitize(html, { ADD_ATTR: ['target'] });
  if (typeof document === 'undefined') {
    return clean;
  }

  const template = document.createElement('template');
  template.innerHTML = clean;
  const anchors = Array.from(template.content.querySelectorAll('a[target]'));
  anchors.forEach((anchor) => {
    const target = anchor.getAttribute('target');
    if (target !== '_blank') {
      anchor.removeAttribute('target');

      return;
    }

    const relValues = new Set(anchor.getAttribute('rel')?.split(/\s+/).filter(Boolean) ?? []);
    relValues.add('noopener');
    relValues.add('noreferrer');
    anchor.setAttribute('rel', Array.from(relValues).join(' '));
  });

  return template.innerHTML;
};

export const getHtmlJSX = (
  html: string | undefined,
  stripContainingParagraph?: boolean,
  noBlockMargins?: boolean,
): JSX.Element | JSX.Element[] | string | null => {
  if (stripContainingParagraph) {
    html = stripHtmlParagraph(html);
  }
  if (!html) return null;

  const clean = sanitizeHtml(html);

  return parse(clean, noBlockMargins ? noBlockMarginOptions : themedHtmlOptions);
};

export const htmlRemoveImages = (content: string): string => {
  const output = content.replace(/<img[^>]*>/g, '');

  return truncate(output, 32767);
};

export const removeHtmlTags = (content: string): string => {
  const output = content.replace(/<[^>]*>/g, '');

  return output;
};

export const replaceEscapeCharacters = (content: string): string => {
  let output = content;
  const charMap: { [key: string]: string } = {
    '&nbsp;': ' ',
    '&amp;': '&',
    '&quot;': '"',
    '&lt;': '<',
    '&gt;': '>',
    '&reg;': '®',
    '&copy;': '©',
    '&bull;': '•',
    '&trade;': '™',
    '&#39;': "'",
    '&Agrave;': 'À',
    '&Aacute;': 'Á',
    '&Acirc;': 'Â',
    '&Atilde;': 'Ã',
    '&Auml;': 'Ä',
    '&Aring;': 'Å',
    '&agrave;': 'à',
    '&aacute;': 'á',
    '&acirc;': 'â',
    '&atilde;': 'ã',
    '&auml;': 'ä',
    '&aring;': 'å',
    '&AElig;': 'Æ',
    '&aelig;': 'æ',
    '&szlig;': 'ß',
    '&Ccedil;': 'Ç',
    '&ccedil;': 'ç',
    '&Egrave;': 'È',
    '&Eacute;': 'É',
    '&Ecirc;': 'Ê',
    '&Euml;': 'Ë',
    '&egrave;': 'è',
    '&eacute;': 'é',
    '&ecirc;': 'ê',
    '&euml;': 'ë',
    '&#131;': 'ƒ',
    '&Igrave;': 'Ì',
    '&Iacute;': 'Í',
    '&Icirc;': 'Î',
    '&Iuml;': 'Ï',
    '&igrave;': 'ì',
    '&iacute;': 'í',
    '&icirc;': 'î',
    '&iuml;': 'ï',
    '&Ntilde;': 'Ñ',
    '&ntilde;': 'ñ',
    '&Ograve;': 'Ò',
    '&Oacute;': 'Ó',
    '&Ocirc;': 'Ô',
    '&Otilde;': 'Õ',
    '&Ouml;': 'Ö',
    '&ograve;': 'ò',
    '&oacute;': 'ó',
    '&ocirc;': 'ô',
    '&otilde;': 'õ',
    '&ouml;': 'ö',
    '&Oslash;': 'Ø',
    '&oslash;': 'ø',
    '&#140;': 'Œ',
    '&#156;': 'œ',
    '&#138;': 'Š',
    '&#154;': 'š',
    '&Ugrave;': 'Ù',
    '&Uacute;': 'Ú',
    '&Ucirc;': 'Û',
    '&Uuml;': 'Ü',
    '&ugrave;': 'ù',
    '&uacute;': 'ú',
    '&ucirc;': 'û',
    '&uuml;': 'ü',
    '&#181;': 'µ',
    '&#215;': '×',
    '&Yacute;': 'Ý',
    '&#159;': 'Ÿ',
    '&yacute;': 'ý',
    '&yuml;': 'ÿ',
    '&#176;': '°',
    '&#134;': '†',
    '&#135;': '‡',
    '&#177;': '±',
    '&#171;': '«',
    '&#187;': '»',
    '&#191;': '¿',
    '&#161;': '¡',
    '&#183;': '·',
    '&#149;': '•',
    '&#153;': '™',
    '&#167;': '§',
    '&#182;': '¶',
  };

  for (const [key, value] of Object.entries(charMap)) {
    output = output.replace(new RegExp(key, 'g'), value);
  }

  return output;
};

export const htmlToText = (input: string | undefined): string => {
  if (!input) return '';

  const options = {
    wordwrap: 80,
  };

  const output = convert(input, options);

  return output;
};

export const clearHtmlValue = (
  html: string | undefined,
  stripParagraph: boolean,
  replaceBreaks: boolean,
  linkify: boolean,
): string | undefined => {
  if (!html) return html;

  let newHtml = html;

  //replace new line with </br> for plain text
  if (replaceBreaks && !newHtml.startsWith('<')) {
    newHtml = newHtml.replaceAll('\r\n', '<br/>');
    newHtml = newHtml.replaceAll('\n', '<br/>');
  }

  //linkify
  if (linkify) {
    newHtml = linkifyHtml(newHtml ?? '', { target: '_blank' });
  }

  //strip containing paragraph to reduce padding
  if (stripParagraph) {
    newHtml = stripHtmlParagraph(newHtml) ?? '';
  }

  return newHtml;
};
